// src/components/TakeawaySummary.tsx
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    IconButton,
    Button,
    Snackbar,
    Alert,
    AlertColor,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { TakeawayOrder, Meal, mealService } from '../services';

const TakeawaySummary: React.FC = () => {
    const { t } = useTranslation();
    const [orders, setOrders] = useState<TakeawayOrder[]>([]);
    const [meals, setMeals] = useState<{ [key: number]: Meal }>({});
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: AlertColor;
    }>({
        open: false,
        message: '',
        severity: 'success'
    });
    const [selectedOrder, setSelectedOrder] = useState<TakeawayOrder | null>(null);

    const fetchData = async () => {
        try {
            const [ordersData, mealsData] = await Promise.all([
                mealService.getActiveTakeawayOrders(),
                mealService.getMeals(),
            ]);

            const mealsMap = mealsData.reduce((acc, meal) => {
                acc[meal.id] = meal;
                return acc;
            }, {} as { [key: number]: Meal });

            setOrders(ordersData);
            setMeals(mealsMap);
            setLoading(false);
        } catch (error) {
            console.error('Failed to fetch orders:', error);
            setSnackbar({
                open: true,
                message: 'Failed to load orders',
                severity: 'error'
            });
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleCancelOrder = async (orderId: number) => {
        try {
            await mealService.cancelTakeawayOrder(orderId);
            setSnackbar({
                open: true,
                message: t('takeawayCancelSuccess'),
                severity: 'success'
            });
            fetchData();
        } catch (error) {
            console.error('Failed to cancel order:', error);
            setSnackbar({
                open: true,
                message: 'Failed to cancel order',
                severity: 'error'
            });
        }
    };

    if (loading) {
        return <Typography>Loading...</Typography>;
    }

    return (
        <Box sx={{ maxWidth: 'lg', mx: 'auto', p: 3 }}>
            <Typography variant="h4" gutterBottom>
                {t('takeawaySummary')}
            </Typography>

            {orders.length === 0 ? (
                <Paper sx={{ p: 3 }}>
                    <Typography>{t('noTakeawayOrders')}</Typography>
                </Paper>
            ) : (
                orders.map((order) => (
                    <Paper key={order.id} sx={{ p: 3, mb: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">
                                {t('orderFor')} {order.date}
                            </Typography>
                            <Box>
                                <Button
                                    onClick={() => setSelectedOrder(order)}
                                    variant="outlined"
                                    sx={{ mr: 1 }}
                                >
                                    {t('viewDetails')}
                                </Button>
                                <IconButton
                                    onClick={() => handleCancelOrder(order.id)}
                                    color="error"
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                        </Box>
                        <Typography variant="h6" color="primary">
                            {t('total')}: ${order.totalAmount.toFixed(2)}
                        </Typography>
                    </Paper>
                ))
            )}

            <Dialog
                open={!!selectedOrder}
                onClose={() => setSelectedOrder(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{t('orderDetails')}</DialogTitle>
                <DialogContent>
                    {selectedOrder && (
                        <List>
                            {selectedOrder.items.map((item) => {
                                const meal = meals[item.mealId];
                                return meal ? (
                                    <ListItem key={item.mealId}>
                                        <ListItemText
                                            primary={`${meal.name} x ${item.quantity}`}
                                            secondary={`$${(meal.price * item.quantity).toFixed(2)}`}
                                        />
                                    </ListItem>
                                ) : null;
                            })}
                            <ListItem sx={{ mt: 2 }}>
                                <ListItemText
                                    primary={t('total')}
                                    secondary={`$${selectedOrder.totalAmount.toFixed(2)}`}
                                    primaryTypographyProps={{ variant: 'h6' }}
                                    secondaryTypographyProps={{ variant: 'h6', color: 'primary' }}
                                />
                            </ListItem>
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSelectedOrder(null)}>
                        {t('close')}
                    </Button>
                    <Button
                        onClick={() => {
                            if (selectedOrder) {
                                handleCancelOrder(selectedOrder.id);
                                setSelectedOrder(null);
                            }
                        }}
                        color="error"
                    >
                        {t('cancelOrder')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default TakeawaySummary;